import { DOCUMENT } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { BaseComponent, DialogRef } from '@shared/classes';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ProductCheckTabType } from '@shared/constants';
import { Coupon, Product, StepChallenge } from '@shared/factory';
import {
  DIALOG_DATA,
  DialogCloseButton,
  DialogConfig,
  Dict,
  ProductCheckDialog,
  ProductCheckManualTab,
  ProductCheckProductListTab,
  ProductCheckScanTab,
} from '@shared/models';

import { ManualBarcodeCheckComponent } from './manual-barcode-check/manual-barcode-check.component';
import { ManualProductCheckComponent } from './manual-product-check/manual-product-check.component';
import { ScannerComponent } from './scanner/scanner.component';
import { ProductCheckService } from './product-check.service';

@Component({
  selector: 'b2c-product-check',
  styleUrl: './product-check.component.scss',
  templateUrl: './product-check.component.html',
  imports: [
    ButtonComponent,
    ScannerComponent,
    ManualBarcodeCheckComponent,
    ManualProductCheckComponent,
  ],
  providers: [ProductCheckService],
})
export class ProductCheckComponent extends BaseComponent implements OnInit {
  public config: ProductCheckDialog =
    this.loyaltyChallengeConfig.components.modals.productCheck;

  protected readonly closeButtonConfig: DialogCloseButton =
    this.loyaltyChallengeConfig.components.modals.closeButton;

  protected readonly buttonStyles: Dict = {
    color: this.closeButtonConfig.textColor,
    borderRadius: this.closeButtonConfig.borderRadius,
    backgroundColor: this.closeButtonConfig.backgroundColor,
    fontFamily: this.closeButtonConfig.fontFamily,
    borderColor:
      this.closeButtonConfig.borderColor ||
      this.closeButtonConfig.backgroundColor,
  };

  // Captured once when the dialog opens: input capability doesn't change during
  // a session. Snapshotting (rather than reading the signal live) keeps the tab
  // set stable — otherwise DevTools toggling the pointer/hover media features
  // (e.g. activating the element inspector) would flip it mid-session.
  private readonly isTouchDevice: boolean =
    this.mediaObserverService.isTouchDevice();

  private readonly isAndroid: boolean = this.mediaObserverService.isAndroid();

  // The scan tab relies on a camera, so it is shown only on touch devices — not
  // on a mouse-driven desktop. Resolved once (input capability is fixed for the
  // session); the tab set never changes during the dialog's lifetime.
  public readonly tabs: ProductCheckDialog['tabs'] = this.resolveTabs();

  public readonly scanTab: ProductCheckScanTab | undefined = this.tabs.find(
    (tab): tab is ProductCheckScanTab => tab.type === ProductCheckTabType.SCAN
  );
  public readonly manualTab: ProductCheckManualTab | undefined = this.tabs.find(
    (tab): tab is ProductCheckManualTab =>
      tab.type === ProductCheckTabType.MANUAL
  );
  public readonly productListTab: ProductCheckProductListTab | undefined =
    this.tabs.find(
      (tab): tab is ProductCheckProductListTab =>
        tab.type === ProductCheckTabType.PRODUCT_LIST
    );
  public readonly scanIndex: number = this.tabs.findIndex(
    (tab) => tab.type === ProductCheckTabType.SCAN
  );
  public readonly manualIndex: number = this.tabs.findIndex(
    (tab) => tab.type === ProductCheckTabType.MANUAL
  );
  public readonly productListIndex: number = this.tabs.findIndex(
    (tab) => tab.type === ProductCheckTabType.PRODUCT_LIST
  );
  public readonly hasMultipleTabs: boolean = this.tabs.length > 1;

  public activeTab = signal(0);
  public isScanComplete = signal(false);
  public isChecking = signal(false);
  public checkFailed = signal(false);
  public detectedBarcode = signal<string>('');
  public product = signal<Product | null>(null);

  public data: { item: StepChallenge | Coupon; title: string } =
    inject<DialogConfig>(DIALOG_DATA).data as {
      item: StepChallenge | Coupon;
      title: string;
    };

  private readonly document: Document = inject(DOCUMENT);
  private readonly dialogRef: DialogRef = inject(DialogRef);
  private readonly productCheckService = inject(ProductCheckService);

  ngOnInit(): void {
    if (this.scanTab) {
      this.productCheckService.setCheckMode(this.scanTab.checkMode);
    }
    if (this.data.item instanceof StepChallenge) {
      this.productCheckService.setScope(
        this.data.item.productScope.productScopeId
      );
    } else if (this.data.item instanceof Coupon) {
      this.productCheckService.setCampaignId(this.data.item.campaignId);
      this.productCheckService.setOfferId(this.data.item.offerId);
    }
  }

  tabDataCy(type: ProductCheckTabType): string {
    switch (type) {
      case ProductCheckTabType.SCAN:
        return 'product_check_tab_scan';
      case ProductCheckTabType.MANUAL:
        return 'product_check_tab_manual';
      default:
        return 'product_check_tab_codes';
    }
  }

  onTabChange(index: number): void {
    // Only on a real transition *into* the scan tab. Re-selecting the tab that
    // is already active must change nothing: the click handler fires on the
    // selected tab too, and the arrow/Home/End keys can resolve to the current
    // index, so an unconditional reset would discard a verdict — or hide an
    // eligibility check that carries on running, since `resetScanState` does
    // not cancel the request.
    //
    // The reset is needed on entry because leaving the scan tab destroys the
    // scanner (see the nested `@if` in the template), so returning mounts a
    // fresh one with `ready` true and every failure signal false — no overlay,
    // live camera. A verdict left over from the previous visit would then sit
    // above that live preview with no way to dismiss it, because the restart
    // button it renders behind is the only thing that emits `scanComplete`.
    const isEnteringScanTab =
      index === this.scanIndex && this.activeTab() !== this.scanIndex;
    if (isEnteringScanTab) {
      this.resetScanState();
    }
    this.activeTab.set(index);
  }

  onTabChangeKey(event: Event, index: number): void {
    event.preventDefault();
    this.onTabChange(index);
  }

  onTabArrowKey(event: Event, currentIndex: number): void {
    const count: number = this.tabs.length;
    if (count < 2) {
      return;
    }
    const key: string = (event as KeyboardEvent).key;
    let nextIndex: number;
    switch (key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % count;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + count) % count;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.onTabChange(nextIndex);
    this.document.getElementById(`tab_${nextIndex}`)?.focus();
  }

  onScanSuccess(barcode: string): void {
    this.isScanComplete.set(true);
    this.isChecking.set(true);
    this.detectedBarcode.set(barcode);
    this.checkProduct(barcode);
  }

  onScanComplete(): void {
    this.resetScanState();
  }

  /** Returns the pane to the state a fresh scanner expects to sit beneath. */
  private resetScanState(): void {
    this.product.set(null);
    this.detectedBarcode.set('');
    this.isScanComplete.set(false);
    this.isChecking.set(false);
    this.checkFailed.set(false);
    // A failed product-list fetch would otherwise stick for the modal's
    // lifetime, making every retry re-throw the same stale error.
    this.productCheckService.retryProductsIfFailed();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // Hide the camera-based scan tab on non-touch devices, and on Android when
  // the retailer opted out via `hideOnAndroid`. Never leave the modal with no
  // tab: a scan-only config keeps its scan tab so the body is never empty.
  private resolveTabs(): ProductCheckDialog['tabs'] {
    const tabs: ProductCheckDialog['tabs'] = this.config.tabs.filter(
      (tab) =>
        tab.type !== ProductCheckTabType.SCAN || this.isScanSupported(tab)
    );
    return tabs.length ? tabs : this.config.tabs;
  }

  private isScanSupported(tab: ProductCheckScanTab): boolean {
    return this.isTouchDevice && !(tab.hideOnAndroid && this.isAndroid);
  }

  private checkProduct(barcode: string): void {
    this.isChecking.set(true);
    this.checkFailed.set(false);
    this.productCheckService
      .isEligible(barcode)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isChecking.set(false))
      )
      .subscribe({
        next: (match: Product | null) => {
          this.product.set(match);
        },
        error: (error: unknown) => {
          console.error('Product eligibility check failed', error);
          // Failure ≠ "not eligible": the shopper gets a retry message
          // instead of a false verdict.
          this.checkFailed.set(true);
          this.product.set(null);
        },
      });
  }
}
